import React, { useEffect, useMemo, useState } from "react";
import api from "../../api";
import HrLayout from "../HrLayout";

const PAGE_SIZE = 10;

export default function HrAttendance() {
  const today = new Date();
  const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;

  const [month, setMonth] = useState(defaultMonth);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(1);

  const load = async (p = page) => {
    try {
      setLoading(true);
      setError("");

      const res = await api.get("/hr/attendance", {
        params: {
          month,
          q: q || undefined,
          status: status === "all" ? undefined : status,
          page: p,
          limit: PAGE_SIZE,
        },
      });

      setRows(res.data?.rows || []);
      setTotal(res.data?.total || 0);
      setPage(res.data?.page || p);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || "Failed to load attendance.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    load(1);
    // eslint-disable-next-line
  }, [month, status]);

  const totalPages = Math.max(Math.ceil(total / PAGE_SIZE), 1);

  const exportCSV = () => {
    const headers = [
      "Date",
      "Employee",
      "Code",
      "Email",
      "Department",
      "Check In",
      "Check Out",
      "Work Hours",
      "Status",
    ];

    const lines = rows.map((r) => [
      r.date,
      `${r.first_name || ""} ${r.last_name || ""}`.trim(),
      r.employee_code || "",
      r.email || "",
      r.department_name || "",
      r.check_in ? new Date(r.check_in).toLocaleTimeString() : "",
      r.check_out ? new Date(r.check_out).toLocaleTimeString() : "",
      r.work_hours ?? "",
      r.status ?? "",
    ]);

    const csv = [headers, ...lines]
      .map((arr) => arr.map((x) => `"${String(x ?? "").replaceAll('"', '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hr-attendance-${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const statusChip = (s) => {
    const v = String(s || "").toLowerCase();
    if (v === "present") return "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (v === "absent") return "bg-rose-50 text-rose-700 border-rose-200";
    if (v === "leave") return "bg-blue-50 text-blue-700 border-blue-200";
    if (v === "late") return "bg-purple-50 text-purple-700 border-purple-200";
    return "bg-slate-50 text-slate-700 border-slate-200";
  };

  return (
    <HrLayout title="Attendance">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Attendance</h1>
          <p className="text-sm text-slate-500 mt-1">
            Month-wise attendance with filters and export.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => load(page)}
            className="px-4 py-2 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Refresh
          </button>

          <button
            onClick={exportCSV}
            disabled={loading || rows.length === 0}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-5">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-3">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Month
            </label>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
            />
          </div>

          <div className="md:col-span-6">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Search
            </label>
            <div className="mt-1 flex gap-2">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search employee name, email or code…"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-400"
              />
              <button
                onClick={() => {
                  setPage(1);
                  load(1);
                }}
                className="px-4 py-2 rounded-lg border border-slate-300 bg-white text-sm font-semibold hover:bg-slate-50"
              >
                Search
              </button>
            </div>
          </div>

          <div className="md:col-span-3">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
            >
              <option value="all">All</option>
              <option value="present">Present</option>
              <option value="absent">Absent</option>
              <option value="leave">Leave</option>
              <option value="late">Late</option>
            </select>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span className="px-2 py-1 rounded-full bg-slate-100 border border-slate-200">
            Total Records: <b className="text-slate-700">{total}</b>
          </span>
          <span className="px-2 py-1 rounded-full bg-slate-100 border border-slate-200">
            Page: <b className="text-slate-700">{page}</b> / {totalPages}
          </span>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]">
            <thead className="bg-slate-100 border-b border-slate-200">
              <tr>
                <Th>Date</Th>
                <Th>Employee</Th>
                <Th>Department</Th>
                <Th>Check In</Th>
                <Th>Check Out</Th>
                <Th>Work (min)</Th>
                <Th>Status</Th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <Td><Skeleton /></Td>
                    <Td><Skeleton /></Td>
                    <Td><Skeleton /></Td>
                    <Td><Skeleton /></Td>
                    <Td><Skeleton /></Td>
                    <Td><Skeleton /></Td>
                    <Td><Skeleton /></Td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <Td colSpan={7} className="text-center py-10 text-slate-500">
                    No attendance found.
                  </Td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <Td className="font-medium">{r.date}</Td>
                    <Td>
                      <div className="font-semibold text-slate-800">
                        {(r.first_name || "") + " " + (r.last_name || "")}
                      </div>
                      <div className="text-xs text-slate-500">
                        {r.employee_code || "—"} • {r.email || "—"}
                      </div>
                    </Td>
                    <Td>{r.department_name || "—"}</Td>
                    <Td>{r.check_in ? new Date(r.check_in).toLocaleTimeString() : "—"}</Td>
                    <Td>{r.check_out ? new Date(r.check_out).toLocaleTimeString() : "—"}</Td>
                    <Td>{r.work_hours ? r.work_hours : "—"}</Td>
                    <Td>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-semibold ${statusChip(r.status)}`}>
                        {(r.status || "na").toString().toUpperCase()}
                      </span>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
            <p className="text-xs text-slate-500">
              Showing{" "}
              <b className="text-slate-700">
                {(page - 1) * PAGE_SIZE + 1}
              </b>{" "}
              to{" "}
              <b className="text-slate-700">
                {Math.min(page * PAGE_SIZE, total)}
              </b>{" "}
              of <b className="text-slate-700">{total}</b>
            </p>

            <div className="flex items-center gap-2">
              <button
                disabled={page === 1}
                onClick={() => {
                  const p = Math.max(page - 1, 1);
                  setPage(p);
                  load(p);
                }}
                className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-semibold disabled:opacity-50 hover:bg-slate-50"
              >
                Prev
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => {
                  const p = Math.min(page + 1, totalPages);
                  setPage(p);
                  load(p);
                }}
                className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-semibold disabled:opacity-50 hover:bg-slate-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </HrLayout>
  );
}

/* UI helpers */
function Th({ children }) {
  return (
    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-slate-500">
      {children}
    </th>
  );
}
function Td({ children, className = "", colSpan }) {
  return (
    <td colSpan={colSpan} className={`px-4 py-3 text-slate-700 align-middle ${className}`}>
      {children}
    </td>
  );
}
function Skeleton() {
  return <div className="h-4 w-full bg-slate-100 rounded animate-pulse" />;
}
